import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/ce7c35c8-38b3-46c7-87cc-f9c9273d3bf9/images/8ce8908edc78c205fc2d328b94a22fbd5c1208d4295336c1bf5aa5d1e6b8ea23.png";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-32 pb-16 lg:pt-40 lg:pb-24"
    >
      {/* Decorative background */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.08]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#72B3E4]/20 blur-3xl -z-10"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#055794]/10 blur-3xl -z-10"
      />

      <div className="container-page">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#72B3E4]/15 border border-[#72B3E4]/30 text-[#055794] text-xs font-medium tracking-wide mb-8"
          data-testid="hero-badge"
        >
          <Sparkles size={14} />
          <span className="uppercase tracking-[0.18em]">
            Consultoría &middot; Capacitación &middot; Innovación
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="lg:col-span-8"
          >
            <h1
              data-testid="hero-title"
              className="font-heading text-[44px] leading-[1.02] sm:text-6xl lg:text-[80px] font-extrabold text-[#022140] tracking-tight text-balance"
            >
              Comisión{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Empresarial</span>
                <span className="absolute inset-x-0 bottom-1 h-3 lg:h-4 bg-[#72B3E4]/40 -z-0 rounded-sm" />
              </span>{" "}
              para el Desarrollo Profesional.
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 text-lg lg:text-xl text-[#7F7F7F] max-w-2xl leading-relaxed"
            >
              Impulsamos la competitividad, la innovación, el desarrollo
              tecnológico y la sustentabilidad de las empresas en México con
              soluciones estratégicas personalizadas.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("#contacto")}
                className="group bg-[#022140] hover:bg-[#055794] text-white rounded-full h-12 px-7 text-base font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#055794]/30"
              >
                Solicitar consultoría
                <ArrowRight
                  size={18}
                  className="ml-2 transition-transform group-hover:translate-x-1"
                />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("#servicios")}
                variant="outline"
                className="rounded-full h-12 px-7 text-base font-medium border-2 border-[#022140]/15 text-[#022140] hover:bg-[#022140] hover:text-white hover:border-[#022140] transition-all duration-300"
              >
                Conocer servicios
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-[#72B3E4]/30 to-[#055794]/20 rounded-3xl blur-2xl" />
              <div className="relative bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_30px_60px_-20px_rgba(2,33,64,0.18)]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#055794] font-semibold mb-4">
                  Áreas de impacto
                </p>
                <ul className="space-y-3" data-testid="hero-impact-list">
                  {[
                    "Competitividad nacional e internacional",
                    "Innovación y desarrollo tecnológico",
                    "Sustentabilidad y crecimiento sostenible",
                    "Productividad y eficiencia operativa",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-[15px] text-[#022140]"
                    >
                      <span className="mt-1.5 inline-block h-2 w-2 rounded-full bg-[#055794] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7F7F7F] mb-2">
                    Sede
                  </p>
                  <p className="text-sm text-[#022140] font-medium">
                    Zapopan, Jalisco · México
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
